import { UseRecurlyInstance } from '@recurly/react-recurly';
import { PayPalInstance } from '@recurly/recurly-js/lib/paypal';

import { createMockPayPalInstance, MockPayPalInstance } from './MockPayPalInstance';

export type PayPalOutcome = 'success' | 'error' | 'cancel';

export interface PayPalServiceConfig {
    useMockPayPal: boolean;
    mockOutcome: PayPalOutcome;
}

export interface PayPalServiceInterface {
    getConfig(): PayPalServiceConfig;
    createPayPalInstance(recurlyInstance?: UseRecurlyInstance, clientAuthorization?: string): PayPalInstance;
}

class RealPayPalService implements PayPalServiceInterface {
    private config: PayPalServiceConfig;

    constructor(config: PayPalServiceConfig) {
        this.config = config;
    }

    getConfig(): PayPalServiceConfig {
        return this.config;
    }

    createPayPalInstance(recurlyInstance?: UseRecurlyInstance, clientAuthorization?: string): PayPalInstance {
        if (this.config.useMockPayPal) {
            // Using mock PayPal instance for development
            return createMockPayPalInstance(this.config.mockOutcome);
        }

        if (!recurlyInstance || !clientAuthorization) {
            throw new Error('Recurly instance and client authorization are required for real PayPal integration');
        }

        return recurlyInstance.PayPal({
            braintree: { clientAuthorization },
        });
    }
}

function createPayPalService(): PayPalServiceInterface {
    const useMockPayPal = process.env.NEXT_PUBLIC_MOCK_PAYPAL === 'true';
    const rawOutcome = process.env.NEXT_PUBLIC_MOCK_PAYPAL_OUTCOME;
    const mockOutcome = (['success', 'error', 'cancel'] as Array<string | undefined>).includes(rawOutcome)
        ? (rawOutcome as PayPalOutcome)
        : 'success';

    const config: PayPalServiceConfig = {
        useMockPayPal,
        mockOutcome,
    };

    return new RealPayPalService(config);
}

export const PayPalService = createPayPalService();
